import { tocktix as consumeProto } from '@tock/protos/dist/types/consumer';

export enum Widgets {
  Title = 'title',
  Hero = 'hero-carousel',
  StaticHero = 'static-hero',
  BasicCarousel = 'basic-carousel',
  Grid = 'grid-widget',
  Divider = 'divider',
  CallToAction = 'call-to-action',
  ModalText = 'modal-text',
  Marketing = 'marketing',
  MarketingFeatures = 'marketing-features',
  CollectionPage = 'collection-page',
  IncludedContent = 'included-content',
  // This type ensures compatibility of our new widget data model with our existing system.
  // These types can all be removed once existing widgets are converted to our new model, in favor
  // of using oneof type cases for these purposes + keeping all typing in our proto model instead of the frontend.
  WidgetCreatorWidgetType = 'widget-creator-widget',
}

export enum Modifiers {
  isAlignedCenter = 'isAlignedCenter',
  showDescription = 'showDescription',
  showReleasesInfo = 'showReleasesInfo',
  useWiderSquares = 'useWiderSquares',
  imageUrl = 'imageUrl',
  bodyText = 'bodyText',
  altText = 'altText',
  layout = 'layout',
  contentColumnA = 'contentColumnA',
  contentColumnB = 'contentColumnB',
  density = 'density',
  titleType = 'titleType',
  subtitleDisplayType = 'subtitleDisplayType',
  marketingFeaturesConfigurationName = 'marketingFeaturesConfigurationName',
  includedContentUrl = 'includedContentUrl',
  // modal widget fields
  ribbonText = 'ribbonText',
  linkText = 'linkText',
  modalTitle = 'modalTitle',
  modalBody = 'modalBody',
  // collection page fields
  decorativeHeroImageUrl = 'decorativeHeroImageUrl',
  decorativeHeroImageUrlDesktop = 'decorativeHeroImageUrlDesktop',
  decorativeHeroImageAltText = 'decorativeHeroImageAltText',
  decorativeColorHex = 'decorativeColorHex',
  // basic carousel widget fields
  isFeaturedContent = 'isFeaturedContent',
  callToActionText = 'callToActionText',
}

export type WidgetType =
  | TitleWidget
  | HeroWidget
  | StaticHeroWidget
  | BasicCarouselWidget
  | GridWidget
  | DividerWidget
  | CallToActionWidget
  | ModalTextWidget
  | MarketingWidget
  | MarketingFeaturesWidget
  | CollectionPageWidget
  | IncludedContentWidget
  | WidgetCreatorWidget;

export type WidgetModifiers =
  | TitleWidgetModifiers
  | HeroWidgetModifiers
  | StaticHeroWidgetModifiers
  | BasicCarouselWidgetModifiers
  | GridWidgetModifiers
  | CallToActionWidgetModifiers
  | ModalTextWidgetModifiers
  | MarketingWidgetModifiers
  | MarketingFeaturesWidgetModifiers
  | CollectionPageWidgetModifiers
  | IncludedContentWidgetModifiers;

export enum BreakpointEnum {
  MobileLandscape = 'MOBILE_LANDSCAPE',
  TabletPortrait = 'TABLET_PORTRAIT',
  TabletLandscape = 'TABLET_LANDSCAPE',
  DesktopRegular = 'DESKTOP_REGULAR',
  DesktopWide = 'DESKTOP_WIDE',
}

export const breakpointOptions = [
  { label: 'Mobile', value: BreakpointEnum.MobileLandscape, key: 'm-landscape' },
  { label: 'Tablet', value: BreakpointEnum.TabletLandscape, key: 't-landscape' },
  { label: 'Desktop', value: BreakpointEnum.DesktopRegular, key: 'd-regular' },
];

/////////////////////////////
// WidgetCreatorWidget    //
////////////////////////////
export interface WidgetCreatorWidget extends consumeProto.proto.consumer.Widget {
  renderType: Widgets.WidgetCreatorWidgetType;
}

/////////////////////////////
// TITLE WIDGET            //
/////////////////////////////
export interface TitleWidget extends consumeProto.proto.consumer.Widget {
  renderType: Widgets.Title;
  widgetModifierFields: TitleWidgetModifiers;
}
export interface TitleWidgetModifier {
  key: Modifiers.isAlignedCenter;
  breakpoint: consumeProto.proto.consumer.WidgetModifier.Breakpoint[];
}
export type TitleWidgetModifiers = TitleWidgetModifier[];
export interface TitleWidgetModifierFields {
  isAlignedCenter: consumeProto.proto.consumer.WidgetModifier.Breakpoint[];
}

/////////////////////////////
// HERO WIDGET             //
/////////////////////////////
export interface HeroWidget extends consumeProto.proto.consumer.Widget {
  renderType: Widgets.Hero;
  widgetModifierFields: HeroWidgetModifiers;
}
export interface HeroWidgetModifier {
  key: Modifiers.isAlignedCenter;
  breakpoint: consumeProto.proto.consumer.WidgetModifier.Breakpoint[];
}
export type HeroWidgetModifiers = HeroWidgetModifier[];

/////////////////////////////
// STATIC HERO WIDGET      //
/////////////////////////////
export interface StaticHeroWidget extends consumeProto.proto.consumer.Widget {
  renderType: Widgets.StaticHero;
  widgetModifierFields: StaticHeroWidgetModifiers;
}
export interface StaticHeroWidgetModifier {
  key: Modifiers.imageUrl;
  value: string;
}
export type StaticHeroWidgetModifiers = StaticHeroWidgetModifier[];
export interface StaticHeroWidgetModifierFields {
  imageUrl: string;
}

/////////////////////////////
// BASIC CAROUSEL WIDGET   //
/////////////////////////////
export interface BasicCarouselWidget extends consumeProto.proto.consumer.Widget {
  renderType: Widgets.BasicCarousel;
  widgetModifierFields: BasicCarouselWidgetModifiers;
}
export interface BasicCarouselWidgetModifier {
  key:
    | Modifiers.isAlignedCenter
    | Modifiers.showReleasesInfo
    | Modifiers.subtitleDisplayType
    | Modifiers.titleType
    | Modifiers.isFeaturedContent
    | Modifiers.callToActionText;
  breakpoint?: consumeProto.proto.consumer.WidgetModifier.Breakpoint[];
  value?: string;
}
export type BasicCarouselWidgetModifiers = BasicCarouselWidgetModifier[];

export interface BasicCarouselWidgetModifierFields {
  isAlignedCenter: consumeProto.proto.consumer.WidgetModifier.Breakpoint[];
  showReleasesInfo: consumeProto.proto.consumer.WidgetModifier.Breakpoint[];
  showNeighborhood: boolean;
  titleType: string;
  isFeaturedContent: boolean;
  callToActionText: string;
}

///////////////////////////
//COLLECTION PAGE WIDGET //
///////////////////////////
export interface CollectionPageWidget extends consumeProto.proto.consumer.Widget {
  renderType: Widgets.CollectionPage;
  widgetModifierFields: CollectionPageWidgetModifiers;
}
export interface CollectionPageWidgetModifier {
  key:
    | Modifiers.decorativeHeroImageUrl
    | Modifiers.decorativeHeroImageUrlDesktop
    | Modifiers.decorativeHeroImageAltText
    | Modifiers.decorativeColorHex;
  value?: string;
}
export type CollectionPageWidgetModifiers = CollectionPageWidgetModifier[];
export interface CollectionPageWidgetModifierFields {
  decorativeHeroImageUrl: string;
  decorativeHeroImageUrlDesktop: string;
  decorativeHeroImageAltText: string;
  decorativeColorHex: string;
}

/////////////////////////////
// GRID WIDGET             //
/////////////////////////////
export interface GridWidget extends consumeProto.proto.consumer.Widget {
  renderType: Widgets.Grid;
  widgetModifierFields: GridWidgetModifiers;
}
export interface GridWidgetModifier {
  key:
    | Modifiers.isAlignedCenter
    | Modifiers.showDescription
    | Modifiers.useWiderSquares
    | Modifiers.showReleasesInfo
    | Modifiers.density
    | Modifiers.titleType
    | Modifiers.subtitleDisplayType;
  breakpoint?: consumeProto.proto.consumer.WidgetModifier.Breakpoint[];
  value?: string;
}
export type GridWidgetModifiers = GridWidgetModifier[];
export interface GridWidgetModifierFields {
  isAlignedCenter: string[];
  showDescription: string[];
  useWiderSquares: string[];
  showReleasesInfo: string[];
  density: string;
  titleType: string;
  showNeighborhood: boolean;
}

/////////////////////////////
// DIVIDER WIDGET          //
/////////////////////////////
export interface DividerWidget extends consumeProto.proto.consumer.Widget {
  renderType: Widgets.Divider;
  widgetModifierFields: DividerWidgetModifiers;
}
export interface DividerWidgetModifier {
  key: Modifiers.isAlignedCenter;
  value: string;
}
export type DividerWidgetModifiers = DividerWidgetModifier[];

/////////////////////////////
// CALL TO ACTION WIDGET   //
/////////////////////////////

export interface CallToActionWidget extends consumeProto.proto.consumer.Widget {
  renderType: Widgets.CallToAction;
  widgetModifierFields: CallToActionWidgetModifiers;
}
export interface CallToActionWidgetModifier {
  key: CallToActionWidgetModiferKeys;
  value: string;
}

type CallToActionWidgetModiferKeys =
  | Modifiers.imageUrl
  | Modifiers.bodyText
  | Modifiers.altText
  | Modifiers.layout;

export type CallToActionWidgetModifiers = CallToActionWidgetModifier[];
export interface CallToActionWidgetModifierFields {
  imageUrl: string;
  bodyText: string;
  altText: string;
  layout: string;
}

//////////////////////////
// Modal Text Widget   //
/////////////////////////

export interface ModalTextWidget extends consumeProto.proto.consumer.Widget {
  renderType: Widgets.ModalText;
  widgetModifierFields: ModalTextWidgetModifiers;
}
export interface ModalTextWidgetModifier {
  key: ModalTextWidgetModiferKeys;
  value: string;
}

type ModalTextWidgetModiferKeys =
  | Modifiers.ribbonText
  | Modifiers.linkText
  | Modifiers.modalTitle
  | Modifiers.modalBody;

export type ModalTextWidgetModifiers = ModalTextWidgetModifier[];
export interface ModalTextWidgetModifierFields {
  ribbonText: string;
  linkText: string;
  modalTitle: string;
  modalBody: string;
}

/////////////////////////////
// MARKETING WIDGETS       //
/////////////////////////////

export interface MarketingWidget extends consumeProto.proto.consumer.Widget {
  renderType: Widgets.Marketing;
  widgetModifierFields: MarketingWidgetModifiers;
}

export interface MarketingWidgetModifier {
  key: MarketingWidgetModifierKeys;
  value: string;
}

type MarketingWidgetModifierKeys = Modifiers.contentColumnA | Modifiers.contentColumnB;

export type MarketingWidgetModifiers = MarketingWidgetModifier[];
export interface MarketingWidgetModifierFields {
  contentColumnA: string;
  contentColumnB: string;
}

//////////////////////////////////
// MARKETING FEATURES WIDGET    //
//////////////////////////////////

export interface MarketingFeaturesWidget extends consumeProto.proto.consumer.Widget {
  renderType: Widgets.MarketingFeatures;
  widgetModifierFields: MarketingFeaturesWidgetModifiers;
}

export interface MarketingFeaturesWidgetModifier {
  key: MarketingFeaturesWidgetModifierKeys;
  value: string;
}

type MarketingFeaturesWidgetModifierKeys = Modifiers.marketingFeaturesConfigurationName;

export type MarketingFeaturesWidgetModifiers = MarketingFeaturesWidgetModifier[];
export interface MarketingFeaturesWidgetModifierFields {
  marketingFeaturesConfigurationName: string;
}

//////////////////////////////////
// INCLUDED CONTENT WIDGET      //
//////////////////////////////////

export interface IncludedContentWidget extends consumeProto.proto.consumer.Widget {
  renderType: Widgets.IncludedContent;
  widgetModifierFields: IncludedContentWidgetModifiers;
}

export interface IncludedContentWidgetModifier {
  key: IncludedContentWidgetModifierKeys;
  value: string;
}

type IncludedContentWidgetModifierKeys = Modifiers.includedContentUrl;

export type IncludedContentWidgetModifiers = IncludedContentWidgetModifier[];
export interface IncludedContentWidgetModifierFields {
  includedContentUrl: string;
}

// The keys for the disabled heading elements must be unique, even though they are never really used
// for anything.
export const widgetSelectOptions = [
  {
    key: Widgets.WidgetCreatorWidgetType,
    label: 'Widget-creator widgets',
    value: Widgets.WidgetCreatorWidgetType,
  },
  {
    key: 'named-config-heading',
    label: '───── Named configuration widgets',
    value: '',
    disabled: true,
  },
  { key: Widgets.CollectionPage, label: 'Collection Page', value: Widgets.CollectionPage },
  { key: Widgets.MarketingFeatures, label: 'Marketing Features', value: Widgets.MarketingFeatures },
  {
    key: 'lucene-query-heading',
    label: '───── Lucene query-based widgets',
    value: '',
    disabled: true,
  },
  { key: Widgets.BasicCarousel, label: 'Basic Carousel', value: Widgets.BasicCarousel },
  { key: Widgets.Grid, label: 'Grid', value: Widgets.Grid },
  { key: Widgets.Hero, label: 'Hero Carousel', value: Widgets.Hero },
  {
    key: 'no-data-config-heading',
    label: '───── Widgets that have no extra data config',
    value: '',
    disabled: true,
  },
  { key: Widgets.Divider, label: 'Divider', value: Widgets.Divider },
  { key: Widgets.IncludedContent, label: 'Included content', value: Widgets.IncludedContent },
  { key: Widgets.Marketing, label: 'Marketing', value: Widgets.Marketing },
  { key: Widgets.StaticHero, label: 'Static Hero', value: Widgets.StaticHero },
  { key: Widgets.Title, label: 'Title', value: Widgets.Title },
  { key: 'other-heading', label: '───── Other widgets', value: '', disabled: true },
  { key: Widgets.CallToAction, label: 'Call To Action', value: Widgets.CallToAction },
  { key: Widgets.ModalText, label: 'Modal', value: Widgets.ModalText },
];

export const alignmentSelectionOptions = [
  { key: 'left', label: 'Left', value: 'left' },
  { key: 'center', label: 'Center', value: 'center' },
  { key: 'right', label: 'Right', value: 'right' },
];
